import { type FC } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { useURLSearchParam } from "widgets/hooks";

import styleLayout from "../core/styles/PageLayout.module.scss";

import ButtonHeader from "../core/ButtonHeader.js";
import DataTableContainer from "../core/grid/DataTableContainer.js";
import { ContextWidgetType } from "../core/navigation-panel/ContextWidget.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { type InputTableQuerySpec } from "@/graphql.ts";
import { type SavedViewState } from "@/redux/modules/saved-view.ts";
import { createTableSpecForRootEntityType, createUniqueKeyFromTableSpec } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, VIEW } from "@/settings/settings.ts";

// e.g. sampleType/12/samples?filter=status.name=in=(%27New%27)
// e.g. samples?filter=status.name=in=(%27New%27);type.name==%27SUEZ%20AD%20France%27
const SamplesPage: FC = () => {
  const viewId = useURLViewID();
  const { sampleTypeId } = useParams<"sampleTypeId">(); // we are using a specific URL when the sample type id is provided, because there is no corresponding column filter, and resurrecting a table view would require to target the correct table query specification
  const filter = useURLSearchParam("filter");
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);

  useDocumentTitle("Samples Search");

  const getSamplesPage = () => {
    const tableQuerySpec = sampleTypeId
      ? ({
          id: Number(sampleTypeId),
          idType: DATATABLE_ID_TYPE.ENTITY,
          queryType: ENTITY_TYPE.SAMPLE_TYPE,
          resultType: ENTITY_TYPE.SAMPLE,
        } satisfies InputTableQuerySpec)
      : createTableSpecForRootEntityType(ENTITY_TYPE.SAMPLE); // for backward compatibility, in case sampleType is provided as part of the rsql filter

    return (
      <>
        <PageHeader
          title={"Samples"}
          actionWidgets={[
            <ButtonHeader
              viewType={VIEW.TABLE_VIEW.type}
              tableSpec={tableQuerySpec}
              hideBlastBtn={true}
              key={"samples-page-action-1"}
            />,
          ]}
        />
        <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
          <DataTableContainer
            key={`${createUniqueKeyFromTableSpec(tableQuerySpec)}`}
            tableSpec={tableQuerySpec}
            viewId={viewId}
            tableInitializationOptions={{
              sortExpression: "-created",
              filterExpression: filter,
            }}
          />
        </div>
      </>
    );
  };

  const getContextProps = () => {
    if (viewId && !isSavedViewLoading && view?.id == viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    }
    return null;
  };

  const getNavPanelCategories = (): INavPanelCategory[] => {
    return [
      {
        label: "Search",
        navItems: [
          {
            label: "Samples",
            route: ``,
            pageComponent: getSamplesPage(),
          },
        ],
      },
    ];
  };

  return <PageLayout contextWidgetProps={getContextProps()} navPanelCategories={getNavPanelCategories()} />;
};

export default SamplesPage;
